import React from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Global, css } from '@emotion/react';
import { GrainProvider } from '@flodesk/grain';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import AppRoutes from '@/routes';

const queryClient = new QueryClient();

const globalStyles = css`
  @font-face {
    font-family: 'Flodesk Sans';
    font-weight: 400;
    src:
      url('/fonts/FlodeskSans/FlodeskSans-Regular.woff2') format('woff2'),
      url('/fonts/FlodeskSans/FlodeskSans-Regular.woff') format('woff');
  }
  @font-face {
    font-family: 'Flodesk Sans';
    font-weight: 500;
    src:
      url('/fonts/FlodeskSans/FlodeskSans-Medium.woff2') format('woff2'),
      url('/fonts/FlodeskSans/FlodeskSans-Medium.woff') format('woff');
  }
  @font-face {
    font-family: 'FlotesqueGX';
    font-weight: 400;
    src: url('/fonts/FlotesqueGX/FlotesqueGX_v2.ttf') format('truetype');
  }
  @font-face {
    font-family: 'Flodesk JJannon';
    font-weight: 400;
    font-display: swap;
    src: url('/fonts/FlodeskJJannon/flodesk-jjannon-400.woff2') format('woff2');
  }

  /*
   * Marketing-site brand layer.
   *
   * Grain covers product UI — neutrals plus four status hues — and deliberately
   * carries none of the accent palette or display serif that flodesk.com uses.
   * These values are the live site's own --color-palette-accent-* tokens, so the
   * marketing look sits on top of Grain rather than replacing it.
   */
  :root {
    --fd-serif: 'Flodesk JJannon', 'Times New Roman', serif;
    --fd-sans: 'Flodesk Sans', -apple-system, Arial, sans-serif;

    /* Warm page canvas the accent washes fade into. On Grain's pure white the
       gradients terminate in a visible rectangle. */
    --fd-bg: #f8f8f5;
    --fd-bg-2: #f0f0eb;

    --fd-lavender-20: #e9e7fe;
    --fd-sand-10: #fbf9e8;
    --fd-sand-20: #f1ebc8;
    --fd-sand-70: #6e6848;
    --fd-pistachio-20: #dff0d5;
    --fd-pistachio-40: #a8cc90;
    --fd-pistachio-70: #4e742d;
    --fd-dusty-20: #dfeaff;
    --fd-clay-20: #dad6d2;
    --fd-olive-20: #ebecd5;
    --fd-olive-70: #6d6b1b;

    /* Matching zero-alpha stops, so the washes fade out instead of to grey. */
    --fd-lavender-20-fade: rgba(233, 231, 254, 0);
    --fd-sand-20-fade: rgba(241, 235, 200, 0);
    --fd-pistachio-20-fade: rgba(223, 240, 213, 0);
    --fd-dusty-20-fade: rgba(223, 234, 255, 0);
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  body {
    margin: 0;
    background-color: var(--fd-bg);
  }
`;

export default function App() {
  return (
    <GrainProvider>
      <QueryClientProvider client={queryClient}>
        <div className="grn-context">
          <BrowserRouter>
            <Global styles={globalStyles} />
            <AppRoutes />
          </BrowserRouter>
        </div>
      </QueryClientProvider>
    </GrainProvider>
  );
}
